import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, Input, Label, Alert, Button
} from 'reactstrap';
import moment from 'moment';
import { SaveButton } from 'components/buttons';
import { Select, PhoneNumber, Date } from 'components/inputs';
import { MissionTypeSelect } from 'components/selects';
import { BasePlatoonRow } from 'pages/base-managment/soldiers/components';
import { FontAwesome } from 'components/ui';
import { toast } from 'react-toastify';
import { onInputChange } from 'functions/events';
import { isAdmin } from 'functions/login';
import { getParents, createFamily } from 'store/base/parents/operations';
import { allMissionLanguages } from 'data/languages.json';

const blankChild = ( school_id, last = '' ) => ({
  first: '',
  last: last || '',
  first_he: '',
  last_he: '',
  gender: 'M',
  dob: undefined,
  school_type_id: undefined,
  lang_id: undefined,
  class_id: undefined,
  school_id
});

const initialState = ( school_id ) => ({
  father: '',
  mother: '',
  last: '',
  email: '',
  father_cell: '',
  mother_cell: '',
  children: [ blankChild( school_id ) ],
  error: false,
  saving: false
});

class AddFamilyModal extends Component {

  state = initialState( this.props.login && this.props.login.school_id );

  componentDidUpdate( prevProps ) {
    if ( this.props.isOpen && !prevProps.isOpen ) {
      // HQ/INST choose school per child; BC/CAMP use login school
      const school_id = isAdmin( this.props.login.code )
        ? undefined
        : this.props.login.school_id;
      this.setState( initialState( school_id ) );
    }
  }

  onUpdate = update => this.setState( update );

  onChange = onInputChange( this.onUpdate );

  updateChild = ( index, updates ) => {
    const children = this.state.children.map( ( child, i ) =>
      i === index ? { ...child, ...updates } : child
    );
    this.setState({ children });
  };

  onChildInput = index => ({ target }) =>
    this.updateChild( index, { [target.name]: target.value } );

  onChildDate = index => date =>
    this.updateChild( index, {
      dob: date ? date.format( 'YYYY-MM-DD HH:mm:ss' ) : undefined
    });

  onChildSelect = index => key => option => {
    const updates = { [key]: option ? option.value : undefined };
    // Changing school clears platoon so PlatoonSelect reloads for the new base
    if ( key === 'school_id' ) {
      updates.class_id = undefined;
    }
    this.updateChild( index, updates );
  };

  addChild = () => {
    const { login } = this.props;
    const school_id = isAdmin( login.code )
      ? undefined
      : login.school_id;
    this.setState({
      children: [
        ...this.state.children,
        blankChild( school_id, this.state.last )
      ]
    });
  };

  removeChild = index => {
    if ( this.state.children.length <= 1 ) {
      return this.setState({ error: 'At least one child is required.' });
    }
    this.setState({
      children: this.state.children.filter( ( _, i ) => i !== index )
    });
  };

  validate = () => {
    const { father, mother, last, email, children } = this.state;
    const { login } = this.props;
    if ( !email ) return 'Please enter a valid E-mail address.';
    if ( father === '' && mother === '' )
      return "Please enter either the father or mother's name.";
    if ( !last || last.length < 3 )
      return 'Please enter a last name with 3 or more letters.';
    if ( !children.length ) return 'Please add at least one child.';

    for ( let i = 0; i < children.length; i++ ) {
      const n = i + 1;
      const c = children[i];
      if ( !c.first || c.first.length < 3 || !c.last || c.last.length < 3 )
        return `Child #${n}: first and last name must be 3+ letters.`;
      if ( !c.dob ) return `Child #${n}: date of birth is required.`;
      if ( isAdmin( login.code ) && !c.school_id )
        return `Child #${n}: school/base is required.`;
      if ( !c.class_id ) return `Child #${n}: platoon is required.`;
      if ( !c.school_type_id ) return `Child #${n}: mission type is required.`;
      if ( !c.lang_id ) return `Child #${n}: mission language is required.`;
    }
    return false;
  };

  onSubmit = e => {
    e.preventDefault();
    const error = this.validate();
    if ( error ) return this.setState({ error });

    this.setState({ saving: true, error: false });
    const {
      father, mother, last, email, father_cell, mother_cell, children
    } = this.state;

    this.props.createFamily({
      father, mother, last, email, father_cell, mother_cell, children
    })
      .then( () => {
        this.props.toggle();
        this.props.getParents();
        toast.success( 'Family created successfully.' );
        const school_id = isAdmin( this.props.login.code )
          ? undefined
          : this.props.login.school_id;
        this.setState( initialState( school_id ) );
      })
      .catch( err => {
        const message = ( err && err.message ) || 'Could not create family.';
        this.setState({ error: message });
        if ( !this.props.isOpen ) toast.error( message );
      })
      .then( () => this.setState({ saving: false }) );
  };

  renderChild = ( child, index ) => {
    const { login } = this.props;
    const showHebrew = login.inst_id !== 10;

    return (
      <div
        key={ index }
        style={{
          border: '1px solid #dee2e6',
          borderRadius: 4,
          padding: 12,
          marginBottom: 12
        }}
      >
        <Row style={{ marginBottom: 8, alignItems: 'center' }}>
          <Col>
            <strong>Child #{ index + 1 }</strong>
          </Col>
          <Col xs='auto'>
            <Button
              type='button'
              color='link'
              className='text-danger'
              onClick={ () => this.removeChild( index ) }
              disabled={ this.state.children.length <= 1 }
            >
              <FontAwesome icon='trash' /> Remove
            </Button>
          </Col>
        </Row>

        <Row>
          <Col sm='6'>
            <Label htmlFor={ `first-${index}` }>First Name</Label>
            <Input
              id={ `first-${index}` }
              name='first'
              required
              value={ child.first }
              onChange={ this.onChildInput( index ) }
              pattern="^[a-zA-Z\s\-'&quot;]{3,60}$"
              title='3-60 English letters'
            />
          </Col>
          <Col sm='6'>
            <Label htmlFor={ `last-${index}` }>Last Name</Label>
            <Input
              id={ `last-${index}` }
              name='last'
              required
              value={ child.last }
              onChange={ this.onChildInput( index ) }
              pattern="^[a-zA-Z\s\-'&quot;]{3,60}$"
              title='3-60 English letters'
            />
          </Col>
          { showHebrew &&
            <Fragment>
              <Col sm='6' dir='rtl'>
                <Label htmlFor={ `first_he-${index}` }>שם פרטי</Label>
                <Input
                  id={ `first_he-${index}` }
                  name='first_he'
                  value={ child.first_he }
                  onChange={ this.onChildInput( index ) }
                />
              </Col>
              <Col sm='6' dir='rtl'>
                <Label htmlFor={ `last_he-${index}` }>שם משפחה</Label>
                <Input
                  id={ `last_he-${index}` }
                  name='last_he'
                  value={ child.last_he }
                  onChange={ this.onChildInput( index ) }
                />
              </Col>
            </Fragment>
          }
        </Row>

        <Row>
          <Col sm='6'>
            <Label htmlFor={ `dob-${index}` }>Date of Birth</Label>
            <Date
              id={ `dob-${index}` }
              name='dob'
              required
              value={ child.dob ? moment( child.dob ) : undefined }
              onChange={ this.onChildDate( index ) }
              maxDate={ moment() }
              minDate={ moment().subtract( 15, 'years' ) }
            />
          </Col>
          <Col sm='6'>
            <Label htmlFor={ `gender-${index}` }>Gender</Label>
            <Input
              type='select'
              id={ `gender-${index}` }
              name='gender'
              value={ child.gender }
              onChange={ this.onChildInput( index ) }
            >
              <option value='M'>Male</option>
              <option value='F'>Female</option>
            </Input>
          </Col>
        </Row>

        <Row>
          <Col sm='6'>
            <Label>Mission Type</Label>
            <MissionTypeSelect
              required
              id={ `mission_type-${index}` }
              gender={ child.gender }
              value={ child.school_type_id }
              onChange={ this.onChildSelect( index )( 'school_type_id' ) }
            />
          </Col>
          <Col sm='6'>
            <Label>Mission Language</Label>
            <Select
              required
              id={ `mission_lang-${index}` }
              options={ allMissionLanguages }
              selected={ child.lang_id }
              onChange={ this.onChildSelect( index )( 'lang_id' ) }
            />
          </Col>
        </Row>

        <BasePlatoonRow
          required
          code={ login.code }
          classId={ child.class_id }
          schoolId={ child.school_id || login.school_id }
          onChange={ this.onChildSelect( index ) }
        />
      </div>
    );
  };

  render() {
    const { isOpen, toggle } = this.props;
    const {
      mother, father_cell, error, mother_cell,
      father, last, email, saving, children
    } = this.state;
    const inputProps = { onChange: this.onChange };

    return (
      <Modal isOpen={ isOpen } toggle={ toggle } centered size='lg' id='AddFamilyModal'>
        <ModalHeader toggle={ toggle }>Add Family</ModalHeader>
        <form onSubmit={ this.onSubmit }>
          <ModalBody>
            <h6 style={{ marginBottom: 12 }}>Parent</h6>
            <Row>
              <Col xs={6}>
                <Label>E-Mail Address / Username</Label>
                <Input
                  required
                  name='email'
                  value={ email }
                  type='email'
                  { ...inputProps }
                />
              </Col>
              <Col xs={6}>
                <Label>Password</Label>
                <Input name='password' value='p1234' disabled />
              </Col>
              <Col xs={6}>
                <Label>Father</Label>
                <Input
                  name='father'
                  value={ father }
                  { ...inputProps }
                  pattern='^[a-zA-Z\s.]{2,}$'
                  title='Two or more letters'
                />
              </Col>
              <Col xs={6}>
                <Label>Mother</Label>
                <Input
                  name='mother'
                  value={ mother }
                  { ...inputProps }
                  pattern='^[a-zA-Z\s.]{2,}$'
                  title='Two or more letters'
                />
              </Col>
              <Col xs={12}>
                <Label>Last Name</Label>
                <Input
                  required
                  name='last'
                  value={ last }
                  { ...inputProps }
                  pattern='^[a-zA-Z\s.]{3,}$'
                  title='Three or more letters'
                />
              </Col>
              <Col xs={6}>
                <Label>Father Cell Phone</Label>
                <PhoneNumber
                  required
                  name='father_cell'
                  value={ father_cell }
                  { ...inputProps }
                />
              </Col>
              <Col xs={6}>
                <Label>Mother Cell Phone</Label>
                <PhoneNumber
                  required
                  name='mother_cell'
                  value={ mother_cell }
                  { ...inputProps }
                />
              </Col>
            </Row>

            <hr />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h6 style={{ margin: 0 }}>Children</h6>
              <Button type='button' color='secondary' size='sm' onClick={ this.addChild }>
                <FontAwesome icon='plus' /> Add another child
              </Button>
            </div>

            { children.map( this.renderChild ) }

            { error &&
              <Alert color='danger'>{ error }</Alert>
            }
          </ModalBody>
          <ModalFooter>
            <SaveButton text='Create Family' saving={ saving } />
          </ModalFooter>
        </form>
      </Modal>
    );
  }
}

const mapStateToProps = ({ login }) => ({
  login: login.current_login
});

const mapDispatchToProps = { getParents, createFamily };

export default connect( mapStateToProps, mapDispatchToProps )( AddFamilyModal );



// WEBPACK FOOTER //
// ./src/pages/base-managment/parents/AddFamilyModal.jsx